import {IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkCircleLine, CheckmarkLine, InfoLine} from '@dropbox/dig-icons/assets';
import type {DcdOwnershipService} from 'client';
import {Title} from 'components/DSYS/Title';
import {t} from 'i18next';

const APPROVAL_LABELS: Record<string, string> = {
  security_approval: 'app_approval_security',
  privacy_approval: 'app_approval_privacy',
  compliance_approval_status: 'app_approval_compliance',
  vrm_approval: 'app_approval_vrm',
  fcpa_approval: 'app_approval_fcpa',
};

const APPROVAL_ORDER = Object.keys(APPROVAL_LABELS);
const APPROVAL_TOOLTIPS: Record<string, string> = {
  vrm_approval: 'app_approval_vrm_tooltip',
  fcpa_approval: 'app_approval_fcpa_tooltip',
};

const classificationLevelNumber = (level: string) => {
  const match = level.match(/\blevel[\s-]*(\d+)\b/i);
  return match ? Number(match[1]) : undefined;
};

const compareClassificationLevels = (left: string, right: string) => {
  const leftNumber = classificationLevelNumber(left);
  const rightNumber = classificationLevelNumber(right);

  if (leftNumber !== undefined && rightNumber !== undefined && leftNumber !== rightNumber) {
    return leftNumber - rightNumber;
  }
  if (leftNumber !== undefined && rightNumber === undefined) {
    return -1;
  }
  if (leftNumber === undefined && rightNumber !== undefined) {
    return 1;
  }
  return left.localeCompare(right, undefined, {numeric: true, sensitivity: 'base'});
};

const approvalLabel = (key: string) => {
  const translationKey = APPROVAL_LABELS[key];
  if (translationKey) {
    return t(translationKey);
  }
  return key
    .replace(/_approval(?:_status)?$/, '')
    .split('_')
    .map((word) => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(' ');
};

export const DcdOwnershipAppApprovalsCard = ({
  approvals = {},
  dataClassificationLevel,
}: {
  approvals?: DcdOwnershipService['approvals'];
  dataClassificationLevel?: string | null;
}) => {
  const recordedApprovals = Object.entries(approvals)
    .filter(([, isRecorded]) => isRecorded === true)
    .map(([key]) => key)
    .sort((left, right) => {
      const leftIndex = APPROVAL_ORDER.indexOf(left);
      const rightIndex = APPROVAL_ORDER.indexOf(right);
      return (
        (leftIndex === -1 ? APPROVAL_ORDER.length : leftIndex) -
          (rightIndex === -1 ? APPROVAL_ORDER.length : rightIndex) || left.localeCompare(right)
      );
    });
  const dataClassificationLevels = Array.from(
    new Set(
      (dataClassificationLevel ?? '')
        .split(',')
        .map((level) => level.trim())
        .filter(Boolean)
    )
  ).sort(compareClassificationLevels);

  if (recordedApprovals.length === 0 && dataClassificationLevels.length === 0) {
    return null;
  }

  return (
    <Stack
      gap="16"
      className={atoms({
        paddingX: '20',
        paddingY: '16',
        borderColor: 'Border Subtle',
        borderWidth: '1',
        borderStyle: 'Solid',
        borderRadius: 'Medium',
      })}
    >
      {recordedApprovals.length > 0 ? (
        <Stack gap="12">
          <Title size="small" withAccessoryStart={<UIIcon src={CheckmarkCircleLine} />}>
            <Box
              tagName="span"
              display="inline-flex"
              alignItems="center"
              className={atoms({columnGap: '4'})}
            >
              {t('app_recorded_approvals')}
              <Tooltip title={t('app_recorded_approvals_description')} placement="top">
                <IconButton
                  variant="borderless"
                  size="small"
                  aria-label={t('app_recorded_approvals')}
                >
                  <UIIcon src={InfoLine} />
                </IconButton>
              </Tooltip>
            </Box>
          </Title>
          <Split gap="8" wrap="wrap">
            {recordedApprovals.map((approval) => {
              const tooltipKey = APPROVAL_TOOLTIPS[approval];
              const chip = (
                <Chip
                  size="small"
                  variant="success"
                  tabIndex={tooltipKey ? -1 : undefined}
                  style={tooltipKey ? {pointerEvents: 'none', cursor: 'default'} : undefined}
                >
                  <Chip.IconAccessory>
                    <UIIcon src={CheckmarkLine} size="small" />
                  </Chip.IconAccessory>
                  <Chip.Content>{approvalLabel(approval)}</Chip.Content>
                </Chip>
              );
              return (
                <Split.Item key={approval}>
                  {tooltipKey ? (
                    <Tooltip title={t(tooltipKey)} placement="top">
                      <span>{chip}</span>
                    </Tooltip>
                  ) : (
                    chip
                  )}
                </Split.Item>
              );
            })}
          </Split>
        </Stack>
      ) : null}
      {dataClassificationLevels.length > 0 ? (
        <Box
          paddingTop={recordedApprovals.length > 0 ? '12' : undefined}
          className={
            recordedApprovals.length > 0
              ? atoms({borderColor: 'Border Subtle', borderTop: 'Solid'})
              : undefined
          }
        >
          <Stack gap="8">
            <Text isBold>{t('app_data_classification')}</Text>
            <Split gap="8" wrap="wrap">
              {dataClassificationLevels.map((level) => (
                <Split.Item key={level}>
                  <Chip size="small">
                    <Chip.Content>{level}</Chip.Content>
                  </Chip>
                </Split.Item>
              ))}
            </Split>
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
};
